import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import {
  ALLOWED_FILES,
  MAX_FILE_MB,
  type CustomerInfo,
  type FileInfo,
  type FolderInfo,
  type MeResponse,
  type ProjectDetail,
  type ProjectSummary,
  type TemplateInfo,
} from "@handvero/shared";
import { api, ApiFailure, errorText, upload } from "../api";
import { prepareUpload } from "./images";
import { SupportBanner } from "../components/SupportBanner";
import "./app.css";
import { ChatView, TodayPanel } from "./Chat";
import { InstallGuide, isInstalled } from "./InstallGuide";
import { AppBar, EmptyState, Hero, Icon, Island, LOGO, Message, rootTabs, Segments, Sheet, splitNumber, useUnauthorizedRedirect } from "./ui";
import { SettingsProvider, SettingsSheet, useSettings } from "./settings";
import { CalendarView, NotesView } from "./NotesCalendar";
import { AccountView } from "./Account";
import { Mascot } from "./Mascot";
import { GuideHost } from "./Guide";
import { ProjectAccessSheet } from "./ProjectAccess";
import { DeviceCaptureSheet } from "./DeviceCapture";
import { HandwerkerSheet, ReminderSheet } from "./HomeSheets";
import { GlobalHits } from "./GlobalHits";
import { DevicesView } from "./Geraete";
import { QueueBanner, QuickPhoto, ShareTargetPage, sendOrQueue } from "./Teilen";
import { isOffline, queueUpload, QUEUE_EVENT } from "./offlineQueue";

/* ---------- gemeinsame Bausteine ---------- */

interface LastPlace {
  projectId: string;
  projectName: string;
  folderId: string;
  folderPath: string;
}

const LAST_KEY = "hv.app.last";

export function readLast(): LastPlace | null {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    return raw ? (JSON.parse(raw) as LastPlace) : null;
  } catch {
    return null;
  }
}

export function writeLast(place: LastPlace | null) {
  try {
    if (place) localStorage.setItem(LAST_KEY, JSON.stringify(place));
    else localStorage.removeItem(LAST_KEY);
  } catch {
    /* Privater Modus: dann eben ohne Merken. */
  }
}

export function sizeText(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export function folderPath(folders: FolderInfo[], id: string | undefined): FolderInfo[] {
  const out: FolderInfo[] = [];
  let cur = folders.find((f) => f.id === id);
  while (cur) {
    out.unshift(cur);
    const parent = cur.parentId;
    cur = parent ? folders.find((f) => f.id === parent) : undefined;
  }
  return out;
}

/** Dateien eines Ordners samt aller Unterordner. */
export function deepCount(folders: FolderInfo[], id: string): number {
  const own = folders.find((f) => f.id === id)?.fileCount ?? 0;
  return folders.filter((f) => f.parentId === id).reduce((sum, f) => sum + deepCount(folders, f.id), own);
}

/* ---------- Projektliste ---------- */

function Projects({ me }: { me: MeResponse }) {
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const last = readLast();

  useEffect(() => {
    api
      .get<ProjectSummary[]>("/api/projects")
      .then(setProjects)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [onAuth]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!projects) return [];
    return q ? projects.filter((p) => `${p.name} ${p.customer ?? ""} ${p.address ?? ""}`.toLowerCase().includes(q)) : projects;
  }, [projects, query]);

  const lastValid = last && projects?.some((p) => p.id === last.projectId) ? last : null;

  const total = projects?.reduce((n, p) => n + p.fileCount, 0) ?? 0;

  return (
    <>
      <Hero
        kicker={me.company.name}
        title="Projekte"
        sub={projects ? `${projects.length} ${projects.length === 1 ? "Projekt" : "Projekte"} · ${total} Dateien` : undefined}
        actions={<SettingsButton />}
      >
        <div className="hero-search">
          <Icon name="search" />
          <input type="search" placeholder="Projekt, Kunde oder Adresse" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Projekte durchsuchen" />
        </div>
      </Hero>
      <main className="app-main">
        {lastValid && !query && (
          <Link className="resume" to={`/app/p/${lastValid.projectId}/f/${lastValid.folderId}`}>
            <span className="resume-ico">
              <Icon name="camera" />
            </span>
            <span className="resume-text">
              <small>Weiter fotografieren</small>
              <b>{lastValid.projectName}</b>
              <em>{lastValid.folderPath}</em>
            </span>
            <i aria-hidden="true">→</i>
          </Link>
        )}

        {!query && <QuickPhoto />}
        {!query && <InstallHint />}
        {!query && (
          <Link className="app-btn small ghost" to="/app/geraete" style={{ alignSelf: "flex-start", marginBottom: 8 }}>
            Geräte im Haus
          </Link>
        )}
        {!query && <TodayPanel base="/app" compact />}
        <Message>{error}</Message>
        {!projects && !error && <Skeleton rows={4} />}
        {projects && projects.length === 0 && (
          <EmptyState title="Ihr erstes Projekt" text="Legen Sie ein Projekt an. Die Ordner kommen auf Wunsch gleich aus einer Vorlage." words={["Foto", "Ordner", "Fertig"]} />
        )}
        {projects && projects.length > 0 && shown.length === 0 && query.trim().length < 2 && <p className="app-empty">Nichts gefunden für „{query}“.</p>}
        <GlobalHits base="/app" query={query} />
        <ol className="plist">
          {shown.map((p, i) => (
            <li key={p.id} style={{ "--i": i } as React.CSSProperties}>
              <Link to={`/app/p/${p.id}`} className="pcard">
                <span className="pnum">{String(i + 1).padStart(2, "0")}</span>
                <span className="ptext">
                  <b>{p.name}</b>
                  <small>{[p.customer, p.address].filter(Boolean).join(" · ") || "Ohne Kundenangabe"}</small>
                </span>
                {p.restricted && (
                  <span className="plock" title="Nur für ausgewählte Mitarbeiter">
                    <Icon name="lock" />
                  </span>
                )}
                <span className="pcount">
                  <b>{p.fileCount}</b>
                  <small>{p.fileCount === 1 ? "Datei" : "Dateien"}</small>
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </main>
      <Island>
        {rootTabs("projekte")}
        <button className="is-action" onClick={() => setCreating(true)} aria-label="Neues Projekt">
          <Icon name="plus" />
        </button>
      </Island>
      {creating && (
        <NewProject
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            navigate(`/app/p/${p.id}`);
          }}
        />
      )}
    </>
  );
}

export function NewProject({ onClose, onCreated }: { onClose: () => void; onCreated: (p: ProjectDetail) => void }) {
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [templateId, setTemplateId] = useState("builtin:auftrag");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Firmen mit Angebote und Rechnungen wählen einen Kunden aus der Kundenkartei; ohne das Zusatzabo antwortet die Abfrage mit „gesperrt“.
  const [customers, setCustomers] = useState<CustomerInfo[] | null>(null);
  const [customerId, setCustomerId] = useState("");

  useEffect(() => {
    api.get<TemplateInfo[]>("/api/templates").then(setTemplates).catch(() => undefined);
    api.get<CustomerInfo[]>("/api/customers?role=customer").then(setCustomers).catch(() => undefined);
  }, []);
  const chosen = templates.find((t) => t.id === templateId);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError("");
    try {
      onCreated(
        await api.post<ProjectDetail>("/api/projects", {
          name: String(f.get("name") ?? ""),
          ...(customers ? { customerId } : { customer: String(f.get("customer") ?? "") }),
          address: String(f.get("address") ?? ""),
          templateId,
        }),
      );
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Neues Projekt" onClose={onClose}>
      <form className="app-form" onSubmit={submit}>
        <label>
          Projektname
          <input name="name" required maxLength={120} autoFocus placeholder="z. B. Wohnbau Schneider" />
        </label>
        {customers ? (
          <label>
            Kunde
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required>
              <option value="">Bitte wählen …</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.number} · {c.name}
                  {c.city ? `, ${c.city}` : ""}
                </option>
              ))}
            </select>
            <small className="muted-s">Neuer Kunde? Zuerst am PC unter „Kunden“ anlegen.</small>
          </label>
        ) : (
          <label>
            Kunde (freiwillig)
            <input name="customer" maxLength={120} />
          </label>
        )}
        <label>
          Adresse (freiwillig)
          <input name="address" maxLength={200} autoComplete="street-address" />
        </label>
        <label>
          Ordner aus Vorlage
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        {chosen && (
          <p className="tpl-preview">
            {chosen.structure.length ? chosen.structure.map((n) => n.name).join(" · ") : chosen.description}
          </p>
        )}
        <Message>{error}</Message>
        <button className="app-btn" disabled={busy}>
          {busy ? "Wird angelegt …" : "Projekt anlegen"}
        </button>
      </form>
    </Sheet>
  );
}

/* ---------- Projekt mit Ordnern und Dateien ---------- */

interface QueueItem {
  key: number;
  name: string;
  progress: number;
  status: "prepare" | "upload" | "done" | "error" | "queued";
  error?: string;
}

export const FILE_ACCEPT = Object.keys(ALLOWED_FILES)
  .map((e) => `.${e}`)
  .join(",");

function ProjectView({ me }: { me: MeResponse }) {
  const { projectId = "", folderId } = useParams();
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [files, setFiles] = useState<FileInfo[] | null>(null);
  const [error, setError] = useState("");
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [sheet, setSheet] = useState<"folder" | "menu" | "rename" | "access" | "device" | "remind" | "tradesperson" | null>(null);
  const managerView = me.user.role !== "employee";
  const [viewer, setViewer] = useState<number | null>(null);
  const { settings } = useSettings();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef(0);

  const loadProject = useCallback(() => {
    api
      .get<ProjectDetail>(`/api/projects/${projectId}`)
      .then(setProject)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);

  const loadFiles = useCallback(() => {
    if (!folderId) {
      setFiles([]);
      return;
    }
    api
      .get<FileInfo[]>(`/api/folders/${folderId}/files`)
      .then(setFiles)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [folderId, onAuth]);

  useEffect(loadProject, [loadProject]);
  useEffect(() => {
    window.addEventListener(QUEUE_EVENT, loadFiles);
    return () => window.removeEventListener(QUEUE_EVENT, loadFiles);
  }, [loadFiles]);
  useEffect(() => {
    setFiles(null);
    setError("");
    setViewer(null);
    loadFiles();
  }, [loadFiles]);

  const folders = project?.folders ?? [];
  const path = folderPath(folders, folderId);
  const current = path[path.length - 1];
  const children = folders.filter((f) => f.parentId === (folderId ?? null));
  const base = `/app/p/${projectId}`;
  const back = current ? (current.parentId ? `${base}/f/${current.parentId}` : base) : "/app";
  const pathText = path.map((f) => f.name).join(" › ");

  if (project && folderId && !current) {
    return (
      <>
        <Hero kicker={project.name} title="Nicht gefunden" back={base} />
        <main className="app-main">
          <Message>Diesen Ordner gibt es nicht mehr.</Message>
        </main>
      </>
    );
  }

  async function addFiles(list: FileList | null, fromCamera: boolean) {
    if (!list?.length || !folderId || !project || !current) return;
    const picked = Array.from(list);
    const items = picked.map((f) => ({ key: ++keyRef.current, name: f.name, progress: 0, status: "prepare" as const }));
    setQueue((q) => [...q, ...items]);
    const update = (key: number, patch: Partial<QueueItem>) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...patch } : x)));

    // Nacheinander hochladen: schont schwache Mobilfunkverbindungen.
    for (const [i, raw] of picked.entries()) {
      const item = items[i]!;
      try {
        if (raw.size > MAX_FILE_MB * 1024 * 1024 && !raw.type.startsWith("image/")) {
          throw new Error(`Die Datei ist zu groß. Erlaubt sind bis zu ${MAX_FILE_MB} MB je Datei.`);
        }
        const prepared = await prepareUpload(raw, fromCamera);
        update(item.key, { name: prepared.file.name, status: "upload" });
        const form = new FormData();
        if (prepared.thumb) form.append("thumb", prepared.thumb, "vorschau.jpg");
        form.append("file", prepared.file, prepared.file.name);
        const place = { projectId, projectName: project.name, folderId, folderPath: pathText };
        const keepOnDevice = async () => {
          await queueUpload({ ...place, name: prepared.file.name, file: prepared.file, thumb: prepared.thumb });
          update(item.key, { status: "queued", progress: 0 });
        };
        if (navigator.onLine === false) {
          await keepOnDevice();
          continue;
        }
        try {
          const saved = await upload<FileInfo>(`/api/folders/${folderId}/files`, form, (p) => update(item.key, { progress: p }));
          update(item.key, { status: "done", progress: 1 });
          setFiles((fs) => [saved, ...(fs ?? [])]);
          writeLast(place);
        } catch (err) {
          if (!isOffline(err)) throw err;
          await keepOnDevice();
        }
      } catch (err) {
        if (onAuth(err)) return;
        update(item.key, { status: "error", error: errorText(err) });
      }
    }
    loadProject();
    // Erledigte Einträge nach kurzer Zeit ausblenden, Fehler stehen lassen.
    window.setTimeout(() => setQueue((q) => q.filter((x) => x.status !== "done" && x.status !== "queued")), 6000);
  }

  async function removeFolder() {
    if (!current) return;
    try {
      await api.del(`/api/folders/${current.id}`);
    } catch (err) {
      if (err instanceof ApiFailure && err.code === "folder_not_empty") {
        if (!window.confirm(`„${current.name}“ enthält Dateien oder Unterordner. Alles darin endgültig löschen?`)) return;
        try {
          await api.del(`/api/folders/${current.id}?mitInhalt=1`);
        } catch (err2) {
          setSheet(null);
          return setError(errorText(err2));
        }
      } else {
        setSheet(null);
        return setError(errorText(err));
      }
    }
    if (readLast()?.folderId === current.id) writeLast(null);
    setSheet(null);
    loadProject();
    navigate(back, { replace: true });
  }

  const photos = (files ?? []).filter((f) => f.kind === "photo");
  const docs = (files ?? []).filter((f) => f.kind !== "photo");

  const head = current ? splitNumber(current.name) : null;
  const folderTiles = settings.folderView === "kacheln";

  return (
    <>
      <Hero
        kicker={current ? [project?.name, ...path.slice(0, -1).map((f) => splitNumber(f.name).label)].filter(Boolean).join(" › ") : project?.customer || "Projekt"}
        title={head?.label ?? project?.name ?? "Projekt"}
        num={head?.num}
        sub={
          current
            ? `${files?.length ?? 0} ${files?.length === 1 ? "Datei" : "Dateien"}${children.length ? ` · ${children.length} Unterordner` : ""}`
            : project
              ? [project.address, `${project.fileCount} Dateien`].filter(Boolean).join(" · ")
              : undefined
        }
        back={back}
        actions={
          current ? (
            <button className="ab-btn" onClick={() => setSheet("menu")} aria-label="Ordner bearbeiten">
              <Icon name="more" />
            </button>
          ) : (
            <>
              {project && managerView && (
                <button className={`ab-btn${project.restricted ? " on" : ""}`} onClick={() => setSheet("access")} aria-label="Wer sieht das Projekt?">
                  <Icon name="lock" />
                </button>
              )}
              <SettingsButton />
            </>
          )
        }
      >
        {!current && <Segments items={projectSegments(projectId, "ordner")} />}
      </Hero>
      <main className="app-main">
        <Message>{error}</Message>
        {!project && !error && <Skeleton rows={4} />}
        {current && (
          <button className="app-btn small ghost device-btn" onClick={() => setSheet("device")}>
            Gerät erfassen: Typenschild fotografieren
          </button>
        )}
        {current && (
          <div className="device-row">
            <button className="app-btn small ghost" onClick={() => setSheet("remind")}>
              Erinnerung anlegen
            </button>
            <button className="app-btn small ghost" onClick={() => setSheet("tradesperson")}>
              Mein Handwerker
            </button>
          </div>
        )}

        {children.length > 0 && (
          <ul className={folderTiles ? "ftiles" : "list"}>
            {children.map((f, i) => {
              const subCount = folders.filter((x) => x.parentId === f.id).length;
              const total = deepCount(folders, f.id);
              const { num, label } = splitNumber(f.name);
              const meta = `${total} ${total === 1 ? "Datei" : "Dateien"}${subCount > 0 ? ` · ${subCount} Ordner` : ""}`;
              return (
                <li key={f.id} style={{ "--i": i } as React.CSSProperties}>
                  {folderTiles ? (
                    <Link to={`${base}/f/${f.id}`} className={`ftile${total ? "" : " empty"}`}>
                      <span className="ftile-num">{num ?? String(i + 1).padStart(2, "0")}</span>
                      <b>{label}</b>
                      <small>{meta}</small>
                      <i aria-hidden="true">→</i>
                    </Link>
                  ) : (
                    <Link to={`${base}/f/${f.id}`} className="row">
                      <span className="row-ico">{num ?? <Icon name="folder" />}</span>
                      <span className="row-text">
                        <b>{label}</b>
                        <small>{meta}</small>
                      </span>
                      <Icon name="chev" />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {!current && project && children.length === 0 && (
          <EmptyState title="Noch keine Ordner" text="Legen Sie unten einen Ordner an. Fotos landen immer in einem Ordner." words={["Ordner", "anlegen"]} />
        )}
        {!current && children.length > 0 && <p className="hint">Ordner antippen, dann direkt fotografieren.</p>}

        {queue.length > 0 && (
          <ul className="queue" aria-live="polite">
            {queue.map((q) => (
              <li key={q.key} className={q.status}>
                <span className="q-name">{q.name}</span>
                <span className="q-state">
                  {q.status === "prepare" && "wird vorbereitet"}
                  {q.status === "upload" && `${Math.round(q.progress * 100)} %`}
                  {q.status === "done" && "gespeichert"}
                  {q.status === "queued" && "wartet auf Verbindung"}
                  {q.status === "error" && q.error}
                </span>
                <span className="q-bar" style={{ transform: `scaleX(${q.progress})` }} />
                {q.status === "error" && (
                  <button className="q-x" onClick={() => setQueue((all) => all.filter((x) => x.key !== q.key))} aria-label="Hinweis schließen">
                    <Icon name="close" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {current && files === null && <Skeleton tiles />}
        {current && files && files.length === 0 && queue.length === 0 && children.length === 0 && (
          <EmptyState title="Der Ordner ist leer" text="Mit dem Kamera-Knopf landet das Foto direkt hier." words={["Foto", "rein"]} />
        )}

        {photos.length > 0 && (
          <div className="grid" style={{ "--cols": settings.tiles } as React.CSSProperties}>
            {photos.map((f) => (
              <button key={f.id} className="tile" onClick={() => setViewer(files!.indexOf(f))} aria-label={`${f.name} öffnen`}>
                <img src={`/api/files/${f.id}/${f.hasThumb ? "thumb" : "content"}`} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}
        {docs.length > 0 && (
          <ul className="list">
            {docs.map((f) => (
              <li key={f.id}>
                <button className="row" onClick={() => setViewer(files!.indexOf(f))}>
                  <span className="row-ico doc">{f.name.split(".").pop()?.toUpperCase()}</span>
                  <span className="row-text">
                    <b>{f.name}</b>
                    <small>
                      {sizeText(f.size)}
                      {f.createdBy && ` · ${f.createdBy}`}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      <Island>
        <button className="is-tab" onClick={() => setSheet("folder")}>
          <Icon name="plus" />
          <span>Ordner</span>
        </button>
        <button className="is-tab" onClick={() => fileRef.current?.click()} disabled={!current}>
          <Icon name="file" />
          <span>Datei</span>
        </button>
        <button className="is-action cam" onClick={() => cameraRef.current?.click()} disabled={!current} aria-label="Foto aufnehmen">
          <Icon name="camera" />
        </button>
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            void addFiles(e.target.files, true);
            e.target.value = "";
          }}
        />
        <input
          ref={fileRef}
          type="file"
          accept={`image/*,${FILE_ACCEPT}`}
          multiple
          hidden
          onChange={(e) => {
            void addFiles(e.target.files, false);
            e.target.value = "";
          }}
        />
      </Island>

      {sheet === "folder" && (
        <FolderNameSheet
          title={current ? `Neuer Ordner in „${current.name}“` : "Neuer Ordner"}
          submitLabel="Ordner anlegen"
          onClose={() => setSheet(null)}
          onSubmit={async (name) => {
            const f = await api.post<FolderInfo>(`/api/projects/${projectId}/folders`, { name, parentId: folderId ?? null });
            setSheet(null);
            loadProject();
            navigate(`${base}/f/${f.id}`);
          }}
        />
      )}
      {sheet === "rename" && current && (
        <FolderNameSheet
          title="Ordner umbenennen"
          initial={current.name}
          submitLabel="Speichern"
          onClose={() => setSheet(null)}
          onSubmit={async (name) => {
            setProject(await api.patch<ProjectDetail>(`/api/folders/${current.id}`, { name }));
            setSheet(null);
          }}
        />
      )}
      {sheet === "device" && current && (
        <DeviceCaptureSheet
          folderId={current.id}
          folderName={current.name}
          onClose={() => setSheet(null)}
          onSaved={() => {
            loadFiles();
            loadProject();
          }}
        />
      )}
      {sheet === "remind" && <ReminderSheet projectId={projectId} onClose={() => setSheet(null)} />}
      {sheet === "tradesperson" && current && <HandwerkerSheet folderId={current.id} folderName={current.name} onClose={() => setSheet(null)} />}
      {sheet === "access" && project && <ProjectAccessSheet project={project} onClose={() => setSheet(null)} onSaved={setProject} />}
      {sheet === "menu" && current && (
        <Sheet title={current.name} onClose={() => setSheet(null)}>
          <div className="menu">
            <button onClick={() => setSheet("device")}>Gerät erfassen (Typenschild)</button>
            <button onClick={() => setSheet("remind")}>Erinnerung anlegen</button>
            <button onClick={() => setSheet("tradesperson")}>Mein Handwerker (Ordner teilen)</button>
            <button onClick={() => setSheet("rename")}>Umbenennen</button>
            <button className="danger" onClick={() => void removeFolder()}>
              Ordner löschen
            </button>
          </div>
        </Sheet>
      )}
      {viewer !== null && files?.[viewer] && (
        <Viewer
          files={files}
          index={viewer}
          onIndex={setViewer}
          onClose={() => setViewer(null)}
          onDeleted={(id) => {
            setFiles((fs) => (fs ?? []).filter((f) => f.id !== id));
            setViewer(null);
            loadProject();
          }}
        />
      )}
    </>
  );
}

export function FolderNameSheet(props: { title: string; initial?: string; submitLabel: string; onClose: () => void; onSubmit: (name: string) => Promise<void> }) {
  const [name, setName] = useState(props.initial ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={props.title} onClose={props.onClose}>
      <form
        className="app-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
            await props.onSubmit(name);
          } catch (err) {
            setError(errorText(err));
            setBusy(false);
          }
        }}
      >
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoFocus />
        </label>
        <Message>{error}</Message>
        <button className="app-btn" disabled={busy || !name.trim()}>
          {props.submitLabel}
        </button>
      </form>
    </Sheet>
  );
}

export function Viewer({
  files,
  index,
  onIndex,
  onClose,
  onDeleted,
}: {
  files: FileInfo[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  onDeleted: (id: string) => void;
}) {
  const file = files[index]!;
  const [error, setError] = useState("");
  const touchX = useRef<number | null>(null);
  const go = (d: number) => {
    const next = index + d;
    if (next >= 0 && next < files.length) {
      setError("");
      onIndex(next);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  async function remove() {
    if (!window.confirm(`„${file.name}“ endgültig löschen?`)) return;
    try {
      await api.del(`/api/files/${file.id}`);
      onDeleted(file.id);
    } catch (err) {
      setError(errorText(err));
    }
  }

  const src = `/api/files/${file.id}/content`;
  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={file.name}>
      <div className="viewer-bar">
        <button className="ab-btn" onClick={onClose} aria-label="Schließen">
          <Icon name="close" />
        </button>
        <div className="ab-title">
          <b>{file.name}</b>
          <span>
            {new Date(file.createdAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
            {file.createdBy && ` · ${file.createdBy}`}
          </span>
        </div>
      </div>
      <div
        className="viewer-body"
        onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const start = touchX.current;
          const end = e.changedTouches[0]?.clientX;
          if (start !== null && end !== undefined && Math.abs(end - start) > 60) go(end < start ? 1 : -1);
          touchX.current = null;
        }}
      >
        {file.kind === "photo" ? (
          <img src={src} alt={file.name} />
        ) : (
          <div className="viewer-doc">
            <span className="row-ico doc big">{file.name.split(".").pop()?.toUpperCase()}</span>
            <b>{file.name}</b>
            <span>{sizeText(file.size)}</span>
            <a className="app-btn" href={src} target="_blank" rel="noreferrer">
              Öffnen
            </a>
          </div>
        )}
      </div>
      <Message>{error}</Message>
      <div className="viewer-actions">
        <a className="ab-btn" href={`${src}?download=1`} aria-label="Herunterladen">
          <Icon name="download" />
        </a>
        <span className="viewer-count">
          {index + 1} / {files.length}
        </span>
        <button className="ab-btn" onClick={() => void remove()} aria-label="Löschen">
          <Icon name="trash" />
        </button>
      </div>
    </div>
  );
}

/* ---------- Bereiche: Notizen und Kalender ---------- */

function projectSegments(projectId: string, active: "ordner" | "notizen" | "termine" | "chat") {
  const base = `/app/p/${projectId}`;
  return [
    { to: base, label: "Ordner", active: active === "ordner" },
    { to: `${base}/notizen`, label: "Notizen", active: active === "notizen" },
    { to: `${base}/termine`, label: "Termine", active: active === "termine" },
    { to: `${base}/chat`, label: "Chat", active: active === "chat" },
  ];
}

export function SettingsButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="ab-btn" onClick={() => setOpen(true)} aria-label="App einstellen">
        <Icon name="settings" />
      </button>
      {open && <SettingsSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function DevicesPage({ me }: { me: MeResponse }) {
  return (
    <>
      <Hero kicker={me.company.name} title="Geräte im Haus" back="/app" actions={<SettingsButton />} />
      <main className="app-main">
        <DevicesView base="/app" />
      </main>
    </>
  );
}

function AccountPage({ me }: { me: MeResponse }) {
  return (
    <>
      <Hero kicker={me.company.name} title="Mein Konto" back="/app" actions={<SettingsButton />} />
      <main className="app-main">
        <AccountView me={me} />
      </main>
    </>
  );
}

function RootSection({ me, section }: { me: MeResponse; section: "notizen" | "kalender" }) {
  return (
    <>
      <Hero kicker={me.company.name} title={section === "notizen" ? "Notizen" : "Kalender"} actions={<SettingsButton />} />
      {section === "notizen" ? <NotesView nav={rootTabs("notizen")} /> : <CalendarView me={me} nav={rootTabs("kalender")} />}
    </>
  );
}

function ProjectSection({ me, section }: { me: MeResponse; section: "notizen" | "termine" | "chat" }) {
  const { projectId = "" } = useParams();
  const onAuth = useUnauthorizedRedirect();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<ProjectDetail>(`/api/projects/${projectId}`)
      .then(setProject)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);
  return (
    <>
      <Hero kicker={project?.customer || "Projekt"} title={project?.name ?? "Projekt"} back="/app" actions={<SettingsButton />}>
        <Segments items={projectSegments(projectId, section)} />
      </Hero>
      {error ? (
        <main className="app-main">
          <Message>{error}</Message>
        </main>
      ) : section === "chat" ? (
        <ChatView projectId={projectId} />
      ) : section === "notizen" ? (
        <NotesView projectId={projectId} projectName={project?.name} />
      ) : (
        <CalendarView me={me} projectId={projectId} />
      )}
    </>
  );
}

/** Einmaliger Hinweis, solange HandVero noch nicht auf dem Startbildschirm liegt. */
function InstallHint() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      return isInstalled() || localStorage.getItem("hv.install.hidden") === "1";
    } catch {
      return isInstalled();
    }
  });
  if (hidden) return null;
  const hide = () => {
    setHidden(true);
    try {
      localStorage.setItem("hv.install.hidden", "1");
    } catch {
      /* ohne Speicher erscheint der Hinweis beim nächsten Mal wieder */
    }
  };
  return (
    <>
      <div className="resume">
        <button type="button" className="install-hint resume-text" onClick={() => setOpen(true)}>
          <small>Wie eine App nutzen</small>
          <b>HandVero auf den Startbildschirm legen</b>
          <em>Anleitung für iPhone und Android</em>
        </button>
        <button type="button" className="ab-btn small" onClick={hide} aria-label="Hinweis ausblenden">
          <Icon name="close" />
        </button>
      </div>
      {open && <InstallGuide onClose={() => setOpen(false)} />}
    </>
  );
}

/** Platzhalter beim Laden, damit nichts springt. */
function Skeleton({ rows = 3, tiles = false }: { rows?: number; tiles?: boolean }) {
  return (
    <div className={tiles ? "skel skel-tiles" : "skel"} aria-label="Wird geladen" role="status">
      {Array.from({ length: tiles ? 6 : rows }, (_, i) => (
        <i key={i} />
      ))}
    </div>
  );
}

/* ---------- Rahmen ---------- */

/** Beim ersten Öffnen der App in den gewünschten Startbereich springen. */
function StartRedirect() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    let first = true;
    try {
      first = !sessionStorage.getItem("hv.app.started");
      sessionStorage.setItem("hv.app.started", "1");
    } catch {
      /* egal */
    }
    if (!first || location.pathname.replace(/\/$/, "") !== "/app") return;
    const last = readLast();
    if (settings.start === "notizen") navigate("/app/notizen", { replace: true });
    else if (settings.start === "kalender") navigate("/app/kalender", { replace: true });
    else if (settings.start === "zuletzt" && last) navigate(`/app/p/${last.projectId}/f/${last.folderId}`, { replace: true });
    // Nur beim Start, nicht bei jedem Wechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export function MobileApp() {
  const onAuth = useUnauthorizedRedirect();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.classList.add("in-app");
    return () => document.documentElement.classList.remove("in-app");
  }, []);

  // Nur beim Start laden, nicht bei jedem Seitenwechsel.
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;
  useEffect(() => {
    api
      .get<MeResponse>("/api/me")
      .then(setMe)
      .catch((err) => onAuthRef.current(err) || setError(errorText(err)));
  }, []);

  return (
    <SettingsProvider>
      <div className="hv-app">
        {error && !me && (
          <main className="app-main">
            <Message>{error}</Message>
          </main>
        )}
        {!me && !error && <div className="boot" aria-label="HandVero wird geladen">{LOGO}</div>}
        {me && (
          <>
            <StartRedirect />
            <QueueBanner />
            <Routes>
              <Route index element={<Projects me={me} />} />
              <Route path="notizen" element={<RootSection me={me} section="notizen" />} />
              <Route path="kalender" element={<RootSection me={me} section="kalender" />} />
              <Route path="p/:projectId" element={<ProjectView me={me} />} />
              <Route path="p/:projectId/notizen" element={<ProjectSection me={me} section="notizen" />} />
              <Route path="p/:projectId/termine" element={<ProjectSection me={me} section="termine" />} />
              <Route path="p/:projectId/chat" element={<ProjectSection me={me} section="chat" />} />
              <Route path="p/:projectId/f/:folderId" element={<ProjectView me={me} />} />
              <Route path="konto" element={<AccountPage me={me} />} />
              <Route path="geraete" element={<DevicesPage me={me} />} />
              <Route path="teilen" element={<ShareTargetPage />} />
              <Route path="*" element={<Projects me={me} />} />
            </Routes>
            <SupportBanner support={me.support} />
            <Mascot variant="app" />
            <GuideHost me={me} variant="app" />
          </>
        )}
      </div>
    </SettingsProvider>
  );
}
